import React, { useMemo } from 'react';
import Plot from 'react-plotly.js';

const SpeedChart = ({
  segments = [],
  isReversed = false,
  formatStation,
  analysisStart = 0,
  analysisEnd = 0
}) => {
  const speedData = useMemo(() => {
    return segments.map(seg => ({
      distance: seg.from_station_ft ?? 0,
      speed: seg.exception ? null : (seg.avg_speed_mph ?? 0)
    })).sort((a, b) => a.distance - b.distance);
  }, [segments]);

  const { yMin, yMax } = useMemo(() => {
    const vals = speedData.map(d => d.speed).filter(v => v !== null && v !== undefined && v > 0);
    if (vals.length === 0) return { yMin: 0, yMax: 50 };
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    const range = max - min || 10;
    const pad = range * 0.3;
    // Round to nearest 5
    return {
      yMin: Math.max(0, Math.floor((min - pad) / 5) * 5),
      yMax: Math.ceil((max + pad) / 5) * 5
    };
  }, [speedData]);

  const { tickVals, tickText } = useMemo(() => {
    const lo = Math.min(analysisStart, analysisEnd);
    const hi = Math.max(analysisStart, analysisEnd);
    const span = hi - lo;
    const step = span > 5000 ? 1000 : span > 2000 ? 500 : span > 500 ? 100 : 50;
    const vals = [];
    const texts = [];
    for (let v = Math.ceil(lo / step) * step; v <= hi; v += step) {
      vals.push(v);
      texts.push(formatStation(v));
    }
    return { tickVals: vals, tickText: texts };
  }, [analysisStart, analysisEnd, formatStation]);

  const trace = useMemo(() => ({
    x: speedData.map(d => d.distance),
    y: speedData.map(d => d.speed),
    type: 'scatter',
    mode: 'lines',
    line: { color: '#f97316', width: 1.5, shape: 'spline' },
    fill: 'tozeroy',
    fillcolor: 'rgba(249, 115, 22, 0.1)',
    hovertemplate: '<b>Sta:</b> %{customdata}<br><b>Speed:</b> %{y:.1f} mph<extra></extra>',
    customdata: speedData.map(d => formatStation(d.distance)),
    connectgaps: false,
  }), [speedData, formatStation]);

  const layout = useMemo(() => ({
    xaxis: {
      title: { text: 'Station', font: { size: 10, color: '#9ca3af' } },
      showgrid: true,
      gridcolor: '#e5e7eb',
      tickmode: 'array',
      tickvals: tickVals,
      ticktext: tickText,
      tickangle: -45,
      tickfont: { size: 9 },
      range: isReversed ? [analysisStart, analysisEnd] : [analysisStart, analysisEnd],
      autorange: false,
    },
    yaxis: {
      title: { text: 'Speed (mph)', font: { size: 10 } },
      showgrid: true,
      gridcolor: '#e5e7eb',
      range: [yMin, yMax],
      autorange: false,
      fixedrange: true,
    },
    showlegend: false,
    margin: { l: 50, r: 15, t: 10, b: 50 },
    hovermode: 'x',
    autosize: true,
    plot_bgcolor: '#fafafa',
    paper_bgcolor: '#ffffff',
    dragmode: 'zoom',
  }), [tickVals, tickText, isReversed, analysisStart, analysisEnd, yMin, yMax]);

  const config = useMemo(() => ({
    displayModeBar: true,
    displaylogo: false,
    responsive: true,
    scrollZoom: true,
    modeBarButtonsToRemove: ['lasso2d', 'select2d', 'pan2d'],
  }), []);

  return (
    <div className="bg-white h-[calc(100vh-95px)] flex flex-col">
      <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 h-20">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              Speed Profile
              {isReversed && (
                <span className="text-sm font-normal text-blue-600 ml-2">(Reverse Direction)</span>
              )}
            </h3>
            <p className="text-sm text-gray-500 mt-0.5">
              Vehicle speed throughout measurement run
            </p>
          </div>
        </div>
      </div>
      <div className="flex-1 p-6">
        <div className="h-full">
          <Plot
            data={[trace]}
            layout={layout}
            config={config}
            style={{ width: '100%', height: '100%' }}
            useResizeHandler
          />
        </div>
      </div>
    </div>
  );
};

export default SpeedChart;
